<script>
    // Packages
    import {useForm} from '@inertiajs/vue3';
    import {ref, reactive, watch} from 'vue';
    import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'

    // Components
    import SlideOutErrors from "@/Components/SlideOutErrors";

    // Icons
    import {XIcon} from '@heroicons/vue/outline'

    export default {
        components: {
            XIcon,
            Dialog,
            DialogTitle,
            DialogOverlay,
            TransitionRoot,
            SlideOutErrors,
            TransitionChild,
        },
        setup(prop, context) {

            const show = ref(false)

            const showErrors = ref(false)

            const updating = false;

            const trading_details = ref(false);
            
            const currencies = ref(false);

            const form = useForm({
                id: null,
                trading_name: null,
                display_name: null,
                stand_alone_entity: null,
                related_trading_entity_id: null,
                company_registration: null,
                company_vat_number: null,
                unit4_element2: null,
                unit4_element3: null,
                revenue_invoice_email: null,
                supplier_invoice_inbox: null,
                unit4_company_id: null,
                currency_id: null,
                multiple_tax_rates: null,
                tax_wording: null,
            })

            // Watcher function
            watch(() => form.stand_alone_entity, (newVal, oldVal) => {
                if (newVal == 1) {
                    form.related_trading_entity_id = null;
                }
            }, { deep: true })

            function submit() {
                if (this.updating) {
                    this.form.post(route('admin.settings.trading-details.trading-detail.update', [this.form.id]), {
                        onSuccess: page => {
                            this.show = false;
                            this.showErrors = false;
                            this.form.reset();
                        },
                        onError: errors => {
                            this.showErrors = true;
                        }
                    });
                } else {
                    this.form.post(route('admin.settings.trading-details.create'), {
                        onSuccess: page => {
                            this.show = false;
                            this.showErrors = false;
                            this.form.reset();
                        },
                        onError: errors => {
                            this.showErrors = true;
                        }
                    });
                }
            }

            function close() {
                this.form.reset();
                this.form.clearErrors();
                this.show = false;
            }

            function open() {
                this.form.clearErrors();
                this.show = true;
            }

            return {
                show,
                form,
                open,
                close,
                submit,
                updating,
                showErrors,
                trading_details,
                currencies
            }
        }
    }
</script>
<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-2xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl" @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white" v-if="form.id">
                                                Update {{ form.trading_name }}
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white" v-else>
                                                Create Trading Detail
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button" class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300" v-if="form.id">
                                                Update any fields below to update the trading detail.
                                            </p>
                                            <p class="text-sm text-indigo-300" v-else>
                                                Get started by filling in the information below to create a new trading detail.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                                        Trading Detail Information
                                                    </h3>
                                                </div>
                                                <div class="col-span-2">
                                                    <label for="trading-name" class="block text-sm text-gray-900 required">
                                                        Trading Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="trading-name" v-model="form.trading_name"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.trading_name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-2">
                                                    <label for="display-name" class="block text-sm text-gray-900 required">
                                                        Display Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="display-name" v-model="form.display_name"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.display_name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="stand-alone-entity" class="block text-sm text-gray-900 required">
                                                        Stand Alone Entity
                                                    </label>
                                                    <div class="mt-1">
                                                        <select v-model="form.stand_alone_entity"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.stand_alone_entity}"
                                                            name="stand_alone_entity" id="stand_alone_entity">
                                                            <option :selected="form.stand_alone_entity == false" value="0">No</option>
                                                            <option :selected="form.stand_alone_entity == true" value="1">Yes</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="related-trading-entity" class="block text-sm text-gray-900 required">
                                                        Related Trading Entity
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select v-model="form.related_trading_entity_id" :options="trading_details" :disabled="form.stand_alone_entity == 1"
                                                            label="trading_name" :reduce="trading_detail => trading_detail.id" placeholder="Related Trading Entity"
                                                            class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                                            :class="{'border-red-500': form.errors['form.errors.related_trading_entity_id']}" />
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="company-registration" class="block text-sm text-gray-900 required">
                                                        Company Registration
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="company-registration" v-model="form.company_registration"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.company_registration}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="company-vat-number" class="block text-sm text-gray-900 required">
                                                        Company VAT Number
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="company-vat-number" v-model="form.company_vat_number"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.company_vat_number}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="multiple-tax-rates" class="block text-sm text-gray-900">
                                                        Multiple Tax Rates Allowed for Service Lines
                                                    </label>
                                                    <div class="mt-1">
                                                        <select v-model="form.multiple_tax_rates"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.multiple_tax_rates}"
                                                            name="multiple_tax_rates" id="multiple_tax_rates">
                                                            <option :selected="form.multiple_tax_rates == 0" value="0">No</option>
                                                            <option :selected="form.multiple_tax_rates == 1" value="1">Yes</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="unit-4-element-2" class="block text-sm text-gray-900 required">
                                                        Unit 4 Element 2
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="unit-4-element-2" v-model="form.unit4_element2"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.unit4_element2}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="unit-4-element-3" class="block text-sm text-gray-900 required">
                                                        Unit 4 Element 3
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="unit-4-element-2" v-model="form.unit4_element3"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.unit4_element3}"
                                                        />
                                                    </div>
                                                </div>
                                                <div v-if="form.stand_alone_entity == 1" class="col-span-1">
                                                    <label for="unit4-company-id" class="block text-sm text-gray-900 required">
                                                        Unit 4 Company ID
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="unit4-company-id" v-model="form.unit4_company_id"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.unit4_company_id}"
                                                        />
                                                    </div>
                                                </div>
                                                <div v-if="form.stand_alone_entity == 1" class="col-span-1">
                                                    <label for="currency_id" class="block text-sm text-gray-900 required">
                                                        Currency
                                                    </label>
                                                    <v-select v-model="form.currency_id" :options="currencies"
                                                        label="code" :reduce="currency => currency.id" placeholder="Currency"
                                                        class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                                        :class="{'border-red-500': form.errors['form.errors.currency_id']}"
                                                    />
                                                </div>
                                                <div v-if="form.stand_alone_entity == 1" class="col-span-1">
                                                    <label for="revenue-invoice-email" class="block text-sm text-gray-900 required">
                                                        Revenue Invoice Email
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="revenue-invoice-email" v-model="form.revenue_invoice_email"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.revenue_invoice_email}"
                                                        />
                                                    </div>
                                                </div>
                                                <div v-if="form.stand_alone_entity == 1" class="col-span-1">
                                                    <label for="supplier-invoice-inbox" class="block text-sm text-gray-900 required">
                                                        Supplier Invoice Inbox
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="supplier-invoice-inbox" v-model="form.supplier_invoice_inbox"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.supplier_invoice_inbox}"
                                                        />
                                                    </div>
                                                </div>
                                                <div v-if="form.stand_alone_entity == 1" class="col-span-1">
                                                    <label for="tax-wording" class="block text-sm text-gray-900 required">
                                                        Tax Wording
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="tax-wording" v-model="form.tax_wording"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.tax_wording}"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>